// Platform admin dashboard.
//
// Cross-org rollup of usage, cost, and health signals across all three
// environments (dev/staging/prod). Gated to the platform-admin allowlist on
// the server side; non-admins get a 404 from every route and never see this
// page render anything beyond the auth check.
//
// Three independent data loads per env:
//   - /api/admin/totals     → hero strip (org count, subjects, spend, etc.)
//   - /api/admin/orgs       → main org rollup table
//   - /api/admin/failed-runs → recent errors across all orgs
//
// Env switching: a tab bar across the top swaps the env query param. The
// available-envs route tells us which envs this hub deployment has keys
// for; tabs for unavailable envs are hidden entirely.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { UptimeRow } from './admin/UptimeRow';
import { AttentionBlock, type StuckRunRow, type QuotaPressureRow } from './admin/AttentionBlock';
import {
  TrendsBlock,
  type DayPoint,
  type ProviderMixRow,
  type AuthFunnel,
  type SubscriptionState,
} from './admin/TrendsBlock';
import { Sparkline } from './admin/Sparkline';
import { DeployGrid } from './admin/DeployGrid';
import { DbHealthBlock } from './admin/DbHealthBlock';
import { EnvStatusStrip } from './admin/EnvStatusStrip';
import { RateLimitHitsBlock } from './admin/RateLimitHitsBlock';

type AdminEnv = 'dev' | 'staging' | 'prod';

interface Totals {
  env: AdminEnv;
  orgs: number;
  activeSubjects: number;
  members: number;
  buildsThisMonth: number;
  generatesThisMonth: number;
  builderCallsThisMonth: number;
  studioSpendUsd: number;
  builderSpendUsd: number;
  totalSpendUsd: number;
}

interface OrgRollup {
  id: string;
  name: string;
  createdAt: string;
  memberCount: number;
  subjectCount: number;
  subjectsReady: number;
  subjectsBuilding: number;
  subjectsDraft: number;
  buildsThisMonth: number;
  generatesThisMonth: number;
  builderCallsThisMonth: number;
  studioSpendUsd: number;
  builderSpendUsd: number;
  spendThisMonthUsd: number;
  lastActivityAt: string | null;
  hasPersonaBuilder: boolean;
  hasPersonaStudio: boolean;
}

interface FailedRun {
  id: string;
  product: 'persona_builder' | 'persona_studio';
  orgId: string;
  orgName: string;
  subjectId: string | null;
  subjectName: string | null;
  kind: string;
  errorText: string;
  model: string;
  createdAt: string;
}

interface SentryProjectHealth {
  slug: string;
  unresolvedCount: number;
  events7d: number;
  events30d: number;
  events30dByDay: number[];
  lastSeen: string | null;
  topIssue: {
    id: string;
    shortId: string;
    title: string;
    count: number;
    lastSeen: string;
    permalink: string;
  } | null;
  error?: string;
}

interface AttentionData {
  stuckRuns: StuckRunRow[];
  quotaPressure: QuotaPressureRow[];
  stuckThresholdMinutes: number;
  quotaPressureThreshold: number;
}

interface ActivityData {
  signupsTrend: { orgs: DayPoint[]; members: DayPoint[]; windowDays: number };
  aiProviderMix: ProviderMixRow[];
  authFunnel: AuthFunnel;
  subscriptionState: SubscriptionState;
}

interface UptimeData {
  results: Array<{
    product: 'hub' | 'persona_builder' | 'persona_studio';
    env: 'dev' | 'staging' | 'prod';
    url: string;
    status: 'up' | 'slow' | 'down';
    httpStatus: number | null;
    latencyMs: number;
    error?: string;
  }>;
}

interface DeployData {
  configured: boolean;
  reason?: string;
  deploys: Array<{
    app: 'hub' | 'persona_builder' | 'persona_studio';
    env: 'dev' | 'staging' | 'prod';
    projectName: string;
    state: 'READY' | 'ERROR' | 'BUILDING' | 'QUEUED' | 'CANCELED' | 'INITIALIZING' | 'UNKNOWN';
    url: string | null;
    inspectorUrl: string | null;
    createdAt: number | null;
    readyAt: number | null;
    buildingDurationMs: number | null;
    commitSha: string | null;
    commitMessage: string | null;
    branch: string | null;
    error?: string;
  }>;
}

interface EnvStatusData {
  configured: boolean;
  reason?: string;
  envs: Array<{
    env: 'dev' | 'staging' | 'prod';
    configured: boolean;
    migrationVersion: string | null;
    migrationsApplied: number;
    error?: string;
  }>;
  drift: boolean;
}

interface DbHealthData {
  env: AdminEnv;
  configured: boolean;
  reason?: string;
  connectionsActive: number;
  connectionsIdle: number;
  connectionsTotal: number;
  maxConnections: number | null;
  dbSizeBytes: number;
  topTables: Array<{ schema: string; table: string; rowEstimate: number; totalBytes: number }>;
}

interface SentryHealth {
  env: AdminEnv;
  configured: boolean;
  reason?: string;
  org?: string;
  projects?: SentryProjectHealth[];
  topIssues?: Array<{
    id: string;
    shortId: string;
    project: string;
    title: string;
    count: number;
    lastSeen: string;
    permalink: string;
  }>;
}

const ENV_LABELS: Record<AdminEnv, string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

export function AdminPage() {
  const { user, signOut } = useAuth();
  const [availableEnvs, setAvailableEnvs] = useState<AdminEnv[] | null>(null);
  const [env, setEnv] = useState<AdminEnv>('prod');
  const [totals, setTotals] = useState<Totals | null>(null);
  const [orgs, setOrgs] = useState<OrgRollup[] | null>(null);
  const [failedRuns, setFailedRuns] = useState<FailedRun[] | null>(null);
  const [sentry, setSentry] = useState<SentryHealth | null>(null);
  const [attention, setAttention] = useState<AttentionData | null>(null);
  const [activity, setActivity] = useState<ActivityData | null>(null);
  const [uptime, setUptime] = useState<UptimeData | null>(null);
  const [deploys, setDeploys] = useState<DeployData | null>(null);
  const [envStatus, setEnvStatus] = useState<EnvStatusData | null>(null);
  const [dbHealth, setDbHealth] = useState<DbHealthData | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve available envs once, then load data for the default env.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          setError('Not authenticated.');
          return;
        }
        const res = await fetch('/api/admin/available-envs', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (res.status === 404) {
            setError('You do not have admin access.');
          } else {
            setError(`Failed to load admin: HTTP ${res.status}`);
          }
          return;
        }
        const body = (await res.json()) as { envs: AdminEnv[] };
        if (cancelled) return;
        setAvailableEnvs(body.envs);
        // Pick the highest available env that's configured. Prefer prod when present.
        const preferred: AdminEnv =
          body.envs.includes('prod') ? 'prod'
          : body.envs.includes('staging') ? 'staging'
          : body.envs.includes('dev') ? 'dev'
          : 'prod';
        setEnv(preferred);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Failed to initialize admin dashboard.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Platform-wide fetches (uptime + deploys). Run once when admin is
  // authenticated; they don't depend on env so we don't repeat them when
  // the operator switches env tabs.
  useEffect(() => {
    if (!availableEnvs || availableEnvs.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const [uptimeRes, deploysRes, envStatusRes] = await Promise.all([
          fetch(`/api/admin/uptime`, { headers }),
          fetch(`/api/admin/deploys`, { headers }),
          fetch(`/api/admin/env-status`, { headers }),
        ]);
        if (cancelled) return;
        const uptimeBody = uptimeRes.ok ? ((await uptimeRes.json()) as UptimeData) : null;
        const deploysBody = deploysRes.ok ? ((await deploysRes.json()) as DeployData) : null;
        const envStatusBody = envStatusRes.ok ? ((await envStatusRes.json()) as EnvStatusData) : null;
        if (cancelled) return;
        setUptime(uptimeBody);
        setDeploys(deploysBody);
        setEnvStatus(envStatusBody);
      } catch (e) {
        console.error('platform-wide admin fetches failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [availableEnvs]);

  // Env-scoped data — refetches whenever env changes.
  useEffect(() => {
    if (!availableEnvs || availableEnvs.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setTotals(null);
    setOrgs(null);
    setFailedRuns(null);
    setSentry(null);
    setAttention(null);
    setActivity(null);
    setDbHealth(null);

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) throw new Error('Not authenticated');
        const headers = { Authorization: `Bearer ${token}` };
        const q = `?env=${env}`;

        const [
          totalsRes,
          orgsRes,
          failedRes,
          sentryRes,
          attentionRes,
          activityRes,
          dbHealthRes,
        ] = await Promise.all([
          fetch(`/api/admin/totals${q}`, { headers }),
          fetch(`/api/admin/orgs${q}`, { headers }),
          fetch(`/api/admin/failed-runs${q}`, { headers }),
          fetch(`/api/admin/sentry${q}`, { headers }),
          fetch(`/api/admin/attention${q}`, { headers }),
          fetch(`/api/admin/activity${q}`, { headers }),
          fetch(`/api/admin/db-health${q}`, { headers }),
        ]);

        if (cancelled) return;

        if (!totalsRes.ok) throw new Error(`Totals: ${await readError(totalsRes)}`);
        if (!orgsRes.ok) throw new Error(`Orgs: ${await readError(orgsRes)}`);
        if (!failedRes.ok) throw new Error(`Failed runs: ${await readError(failedRes)}`);
        // The auxiliary endpoints (sentry, attention, activity, uptime) are
        // best-effort — log but don't fail the whole dashboard if one is down.

        const totalsBody = (await totalsRes.json()) as Totals;
        const orgsBody = (await orgsRes.json()) as { orgs: OrgRollup[] };
        const failedBody = (await failedRes.json()) as { failedRuns: FailedRun[] };
        const sentryBody = sentryRes.ok ? ((await sentryRes.json()) as SentryHealth) : null;
        const attentionBody = attentionRes.ok ? ((await attentionRes.json()) as AttentionData) : null;
        const activityBody = activityRes.ok ? ((await activityRes.json()) as ActivityData) : null;
        const dbHealthBody = dbHealthRes.ok ? ((await dbHealthRes.json()) as DbHealthData) : null;

        if (cancelled) return;
        setTotals(totalsBody);
        setOrgs(orgsBody.orgs);
        setFailedRuns(failedBody.failedRuns);
        setSentry(sentryBody);
        setAttention(attentionBody);
        setActivity(activityBody);
        setDbHealth(dbHealthBody);
        setError(null);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [env, availableEnvs, refreshKey]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-900 text-slate-200">
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-base font-bold uppercase tracking-widest text-slate-100">
            Platform Admin
          </h1>
          <a
            href="/"
            className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
          >
            ← Back to hub
          </a>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-400">{user?.email}</span>
          <button
            onClick={signOut}
            className="text-slate-400 hover:text-slate-200 uppercase tracking-wider font-bold"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-grow p-6 max-w-7xl mx-auto w-full space-y-6">
        {error ? (
          <div className="border border-red-800 bg-red-950/40 rounded-lg p-4 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        {/* Platform-wide signals — same across all envs, rendered once. */}
        {envStatus && deploys && (
          <EnvStatusStrip envStatus={envStatus} deploys={deploys.deploys} />
        )}
        {uptime && <UptimeRow results={uptime.results} />}
        {deploys && (
          <DeployGrid
            configured={deploys.configured}
            reason={deploys.reason}
            deploys={deploys.deploys}
          />
        )}

        {/* Env-scoped sections below. The tabs control what re-fetches. */}
        {availableEnvs && availableEnvs.length > 0 && (
          <EnvTabs available={availableEnvs} current={env} onChange={setEnv} />
        )}

        {loading ? (
          <div className="text-xs text-slate-500 animate-pulse uppercase tracking-wider font-bold">
            Loading {env}…
          </div>
        ) : (
          <>
            {totals && <TotalsStrip totals={totals} />}
            {attention && (
              <AttentionBlock
                stuckRuns={attention.stuckRuns}
                quotaPressure={attention.quotaPressure}
                stuckThresholdMinutes={attention.stuckThresholdMinutes}
                quotaPressureThreshold={attention.quotaPressureThreshold}
                env={env}
                onQuotaUpdated={() => setRefreshKey((k) => k + 1)}
              />
            )}
            {orgs && <OrgTable rows={orgs} env={env} />}
            {dbHealth && <DbHealthBlock data={dbHealth} />}
            {activity && (
              <TrendsBlock
                signupsTrend={activity.signupsTrend}
                aiProviderMix={activity.aiProviderMix}
                authFunnel={activity.authFunnel}
                subscriptionState={activity.subscriptionState}
                env={env}
              />
            )}
            {failedRuns && <FailedRunsBlock rows={failedRuns} env={env} />}
            <RateLimitHitsBlock env={env} />
            {sentry && <SentryBlock data={sentry} env={env} />}
          </>
        )}
      </main>
    </div>
  );
}

// ── env tabs ────────────────────────────────────────────────────────────

function EnvTabs({
  available,
  current,
  onChange,
}: {
  available: AdminEnv[];
  current: AdminEnv;
  onChange: (env: AdminEnv) => void;
}) {
  return (
    <div className="border-b border-slate-800 flex items-center gap-2 pt-2">
      <span className="text-[9px] uppercase tracking-widest text-slate-600 font-bold mr-2">
        Env →
      </span>
      {available.map((e) => {
        const active = current === e;
        return (
          <button
            key={e}
            onClick={() => onChange(e)}
            className={`relative px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors ${
              active
                ? 'text-indigo-300'
                : 'text-slate-500 hover:text-slate-200'
            }`}
          >
            {ENV_LABELS[e]}
            <span
              className={`absolute left-0 right-0 -bottom-px h-0.5 ${
                active ? 'bg-indigo-400' : 'bg-transparent'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

// ── totals strip ────────────────────────────────────────────────────────

function TotalsStrip({ totals }: { totals: Totals }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
      <StatCard label="Orgs" value={totals.orgs.toLocaleString()} />
      <StatCard label="Members" value={totals.members.toLocaleString()} />
      <StatCard label="Active subjects" value={totals.activeSubjects.toLocaleString()} />
      <StatCard label="Studio builds (mo)" value={totals.buildsThisMonth.toLocaleString()} />
      <StatCard label="Studio gen (mo)" value={totals.generatesThisMonth.toLocaleString()} />
      <StatCard label="Builder calls (mo)" value={(totals.builderCallsThisMonth ?? 0).toLocaleString()} />
      <StatCard
        label="Spend (mo)"
        value={formatUsd(totals.totalSpendUsd)}
        subValue={`Studio ${formatUsd(totals.studioSpendUsd ?? 0)} · Builder ${formatUsd(totals.builderSpendUsd ?? 0)}`}
        accent="emerald"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  subValue,
  accent,
}: {
  label: string;
  value: string;
  subValue?: string;
  accent?: 'emerald' | undefined;
}) {
  return (
    <div className="border border-slate-800 rounded-lg p-3 bg-slate-950/40">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">
        {label}
      </div>
      <div
        className={`text-xl font-black mt-1 ${
          accent === 'emerald' ? 'text-emerald-400' : 'text-slate-100'
        }`}
      >
        {value}
      </div>
      {subValue && (
        <div className="text-[9px] text-slate-500 mt-1 font-mono">{subValue}</div>
      )}
    </div>
  );
}

// ── org table ───────────────────────────────────────────────────────────

function OrgTable({ rows, env }: { rows: OrgRollup[]; env: AdminEnv }) {
  const [sortKey, setSortKey] = useState<keyof OrgRollup>('spendThisMonthUsd');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [query, setQuery] = useState('');

  const sorted = useMemo(() => {
    const filtered = query
      ? rows.filter((r) => r.name.toLowerCase().includes(query.toLowerCase()))
      : rows;
    const direction = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * direction;
      return String(av).localeCompare(String(bv)) * direction;
    });
  }, [rows, sortKey, sortDir, query]);

  const toggleSort = (key: keyof OrgRollup) => {
    if (key === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          Orgs ({rows.length} total · {ENV_LABELS[env]})
        </h3>
        <input
          type="text"
          placeholder="Filter…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 w-40"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/60">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <Th onClick={() => toggleSort('name')} active={sortKey === 'name'} dir={sortDir}>Name</Th>
              <Th onClick={() => toggleSort('memberCount')} active={sortKey === 'memberCount'} dir={sortDir} align="right">Members</Th>
              <Th onClick={() => toggleSort('subjectCount')} active={sortKey === 'subjectCount'} dir={sortDir} align="right">Subjects</Th>
              <Th onClick={() => toggleSort('buildsThisMonth')} active={sortKey === 'buildsThisMonth'} dir={sortDir} align="right">Studio builds</Th>
              <Th onClick={() => toggleSort('generatesThisMonth')} active={sortKey === 'generatesThisMonth'} dir={sortDir} align="right">Studio gen</Th>
              <Th onClick={() => toggleSort('builderCallsThisMonth')} active={sortKey === 'builderCallsThisMonth'} dir={sortDir} align="right">Builder calls</Th>
              <Th onClick={() => toggleSort('spendThisMonthUsd')} active={sortKey === 'spendThisMonthUsd'} dir={sortDir} align="right">Spend (mo)</Th>
              <Th onClick={() => toggleSort('lastActivityAt')} active={sortKey === 'lastActivityAt'} dir={sortDir} align="right">Last activity</Th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Subs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {sorted.map((r) => (
              <tr key={r.id} className="hover:bg-slate-900/40">
                <td className="px-3 py-2">
                  <div className="font-bold text-slate-100">{r.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.id.slice(0, 8)}</div>
                </td>
                <td className="px-3 py-2 text-right text-slate-300 font-mono">{r.memberCount}</td>
                <td className="px-3 py-2 text-right text-slate-300 font-mono">
                  <span className="text-emerald-400">{r.subjectsReady}</span>
                  {r.subjectsBuilding > 0 && <span className="text-amber-400"> ·{r.subjectsBuilding}</span>}
                  {r.subjectsDraft > 0 && <span className="text-slate-500"> /{r.subjectsDraft}</span>}
                  <span className="text-slate-500 ml-1">({r.subjectCount})</span>
                </td>
                <td className="px-3 py-2 text-right text-slate-300 font-mono">{r.buildsThisMonth}</td>
                <td className="px-3 py-2 text-right text-slate-300 font-mono">{r.generatesThisMonth}</td>
                <td className="px-3 py-2 text-right text-slate-300 font-mono">{r.builderCallsThisMonth ?? 0}</td>
                <td className="px-3 py-2 text-right text-emerald-300 font-mono">{formatUsd(r.spendThisMonthUsd)}</td>
                <td className="px-3 py-2 text-right text-slate-400 font-mono">
                  {r.lastActivityAt ? formatRelative(r.lastActivityAt) : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {r.hasPersonaBuilder && <SubBadge label="PB" />}
                    {r.hasPersonaStudio && <SubBadge label="PS" />}
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-[11px] text-slate-500">
                  No orgs match the filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  align,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'right';
}) {
  return (
    <th className={`px-3 py-2 cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`} onClick={onClick}>
      <span className={active ? 'text-indigo-300' : ''}>
        {children}
        {active && <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );
}

function SubBadge({ label }: { label: string }) {
  return (
    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-indigo-900/40 text-indigo-300 border border-indigo-700/40">
      {label}
    </span>
  );
}

// ── failed runs ─────────────────────────────────────────────────────────

function FailedRunsBlock({ rows, env }: { rows: FailedRun[]; env: AdminEnv }) {
  if (rows.length === 0) {
    return (
      <section className="border border-slate-800 rounded-lg p-4">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300 mb-2">
          Recent failed runs ({ENV_LABELS[env]})
        </h3>
        <p className="text-xs text-slate-500 italic">
          No failed runs. The pipeline is healthy.
        </p>
      </section>
    );
  }
  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          Recent failed runs ({rows.length} most recent · {ENV_LABELS[env]})
        </h3>
      </div>
      <ul className="divide-y divide-slate-800">
        {rows.map((r) => (
          <li key={`${r.product}:${r.id}`} className="px-4 py-3 hover:bg-slate-900/40">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold border ${
                      r.product === 'persona_builder'
                        ? 'bg-indigo-900/30 text-indigo-300 border-indigo-700/40'
                        : 'bg-amber-900/30 text-amber-300 border-amber-700/40'
                    }`}
                  >
                    {r.product === 'persona_builder' ? 'PB' : 'PS'}
                  </span>
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-rose-900/30 text-rose-300 border border-rose-700/40 font-bold">
                    {r.kind}
                  </span>
                  {r.subjectName && (
                    <>
                      <span className="text-sm font-bold text-slate-100">{r.subjectName}</span>
                      <span className="text-[11px] text-slate-500">·</span>
                    </>
                  )}
                  <span className="text-[11px] text-slate-400">{r.orgName}</span>
                  {r.model && <span className="text-[10px] text-slate-500 font-mono">{r.model}</span>}
                </div>
                <div className="text-[11px] text-rose-300/90 mt-1 line-clamp-2">
                  {r.errorText || '(no error_text)'}
                </div>
              </div>
              <div className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
                {formatRelative(r.createdAt)}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── sentry block ────────────────────────────────────────────────────────

function SentryBlock({ data, env }: { data: SentryHealth; env: AdminEnv }) {
  if (!data.configured) {
    return (
      <section className="border border-slate-800 rounded-lg p-4">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300 mb-2">
          Sentry health ({ENV_LABELS[env]})
        </h3>
        <p className="text-xs text-slate-500 italic">
          {data.reason || 'Sentry not configured on this hub deployment.'}
        </p>
      </section>
    );
  }

  const projects = data.projects ?? [];
  const totalUnresolved = projects.reduce((a, p) => a + p.unresolvedCount, 0);
  const total7d = projects.reduce((a, p) => a + p.events7d, 0);
  const topIssues = data.topIssues ?? [];

  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          Sentry health ({ENV_LABELS[env]} · {data.org})
        </h3>
        <div className="flex items-center gap-3 text-[10px] text-slate-500 font-mono">
          <span>
            <span className="text-rose-300 font-bold">{totalUnresolved.toLocaleString()}</span>{' '}
            unresolved
          </span>
          <span>·</span>
          <span>
            <span className="text-slate-200 font-bold">{total7d.toLocaleString()}</span>{' '}
            events / 7d
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800">
        {projects.map((p) => (
          <ProjectCard key={p.slug} project={p} org={data.org!} />
        ))}
      </div>

      {topIssues.length > 0 && (
        <div className="border-t border-slate-800">
          <div className="bg-slate-900/40 px-4 py-2 border-b border-slate-800">
            <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Top issues across projects ({ENV_LABELS[env]})
            </h4>
          </div>
          <ul className="divide-y divide-slate-800">
            {topIssues.map((i) => (
              <li key={`${i.project}:${i.id}`} className="px-4 py-2.5 hover:bg-slate-900/40">
                <a
                  href={i.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 font-mono">
                        {i.project}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">{i.shortId}</span>
                      <span className="text-sm text-slate-100 truncate">{i.title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono whitespace-nowrap">
                    <span className="text-rose-300">{i.count.toLocaleString()} ev</span>
                    <span className="text-slate-500">{formatRelative(i.lastSeen)}</span>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function ProjectCard({ project, org }: { project: SentryProjectHealth; org: string }) {
  const projectUrl = `https://sentry.io/organizations/${org}/projects/${project.slug}/`;
  return (
    <div className="bg-slate-950/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <a
          href={projectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-indigo-300"
        >
          {project.slug}
        </a>
        <div className="flex items-center gap-2">
          {project.events30dByDay && project.events30dByDay.length > 0 && (
            <Sparkline
              data={project.events30dByDay}
              width={60}
              height={16}
              color="#fb7185"
              fill="rgba(251,113,133,0.10)"
            />
          )}
          <span className="text-[9px] text-slate-500 font-mono">
            {(project.events30d ?? 0).toLocaleString()} ev/30d
          </span>
        </div>
      </div>
      <div
        className={`text-2xl font-black mt-1 ${
          project.unresolvedCount === 0 ? 'text-emerald-400' : 'text-rose-300'
        }`}
      >
        {project.unresolvedCount.toLocaleString()}
        <span className="text-[9px] uppercase tracking-widest text-slate-500 ml-2 font-bold">
          unresolved
        </span>
      </div>
      {project.error ? (
        <div className="text-[10px] text-rose-300/80 mt-1 line-clamp-2 font-mono">
          {project.error}
        </div>
      ) : project.topIssue ? (
        <a
          href={project.topIssue.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-[11px] text-slate-400 mt-1 truncate hover:text-indigo-300"
          title={project.topIssue.title}
        >
          <span className="text-[10px] text-slate-500 font-mono">
            {project.topIssue.shortId}
          </span>{' '}
          {project.topIssue.title}
        </a>
      ) : (
        <div className="text-[10px] text-slate-500 mt-1 italic">No unresolved issues</div>
      )}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

function formatUsd(cost: number): string {
  if (cost < 0.01) return '<$0.01';
  if (cost < 1) return `$${cost.toFixed(3)}`;
  if (cost < 100) return `$${cost.toFixed(2)}`;
  return `$${Math.round(cost).toLocaleString()}`;
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const min = Math.floor(diff / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo}mo ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
